/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: Hero section for AI Video Generator - Your Fastest Way to Turn Ideas Into Videos
 */
import React, { useRef, useState } from 'react';

import StartButton from './StartButton';

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface VideoData {
  posterUrl: string;
  videoUrl: string;
}

const videos: VideoData[] = [
  {
    posterUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-2-poster.webp`,
    videoUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-2.mp4`,
  },
  {
    posterUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-3-poster.webp`,
    videoUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-3.mp4`,
  },
  {
    posterUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-1-poster.webp`,
    videoUrl: `https://assets.falcocut.ai/ai-video-generator/ideas-to-videos-1.mp4`,
  },
];

const HeroSection: React.FC = () => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const handlePlayClick = (index: number) => {
    const video = videos[index];
    if (!video.videoUrl) return;

    // 暂停其他视频
    videoRefs.current.forEach((v, i) => {
      if (v && i !== index) {
        v.pause();
        v.currentTime = 0;
      }
    });

    const videoEl = videoRefs.current[index];
    if (videoEl) {
      videoEl.play();
      setPlayingIndex(index);
    }
  };

  const handleVideoClick = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.pause();
      video.currentTime = 0;
      setPlayingIndex(null);
    }
  };

  const handleVideoEnded = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.currentTime = 0;
    }
    if (playingIndex === index) {
      setPlayingIndex(null);
    }
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Your Fastest Way to Turn Ideas Into Videos
        </h2>

        {/* 描述 */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-8 pad:mb-10 sm:mb-12 leading-relaxed">
          FalcoCut lets you turn text, images, and concepts into high‑quality videos using
          today’s leading AI models. Whether you want short clips, animated scenes,
          product demos, or social‑ready visuals, the tool makes video creation simple and
          accessible. Just describe what you want, choose a model, and watch your idea
          come to life. Perfect for creators, marketers, educators, and anyone who wants
          fast, flexible video generation.
        </p>

        {/* 视频卡片区域 */}
        <div className="relative mb-10 pad:mb-12 sm:mb-14">
          {/* 背景装饰 - 浅蓝色渐变，被内容撑开 */}
          <div
            className="absolute inset-0 rounded-[32px]"
            style={{
              background:
                'linear-gradient(180deg, rgba(224, 231, 255, 0.6) 0%, rgba(241, 245, 249, 0.8) 100%)',
            }}
          />

          {/* 视频卡片网格 */}
          <div className="relative flex flex-col pad:flex-row items-center justify-center gap-4 pad:gap-6 sm:gap-8 px-4 pad:px-8 sm:px-12 py-8 pad:py-10 sm:py-12">
            {videos.map((video, index) => (
              <div
                key={index}
                className="relative rounded-[20px] overflow-hidden bg-gray-100 shadow-lg w-full max-w-[340px] aspect-[9/16]"
              >
                {video.videoUrl ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    src={video.videoUrl}
                    poster={video.posterUrl}
                    className="w-full h-full object-cover cursor-pointer"
                    onEnded={() => handleVideoEnded(index)}
                    onClick={() => handleVideoClick(index)}
                    playsInline
                  />
                ) : (
                  // 占位图
                  <div
                    className="w-full h-full bg-gray-200"
                    style={{
                      backgroundImage: video.posterUrl
                        ? `url(${video.posterUrl})`
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                )}
                {/* 播放按钮 */}
                {playingIndex !== index && (
                  <button
                    type="button"
                    onClick={() => handlePlayClick(index)}
                    className="absolute inset-0 flex items-center justify-center hover:bg-black/10 transition-colors"
                    aria-label="Play video"
                  >
                    <PlayButton />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA 按钮 */}
        <div className="flex justify-center">
          <StartButton text="Create Your Video Now" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
